import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import styles from './ShoppingCart.module.scss';
import { Button } from '@src/components/ui';
import { CartCount } from '@src/components/ui/CartCount';
import { useCart } from '../../hooks/useCart';
import { calculateTax, getShippingMethods } from '../../api/OrderApi';
import { useAuth } from '../../contexts/AuthContext';
import { formatCurrency } from '../../utils/formatters';
import { useEffectiveClinic } from '../../hooks/useEffectiveClinic';
import { normalizeShippingBreakdown } from '../../utils/shipping';
import { ShippingBreakdown } from '../../components/ShippingBreakdown';
const ShoppingCart = () => {
    const navigate = useNavigate();
    const { isAuthenticated, isClinicAuthorized, isCheckoutAuthorized } = useAuth();
    const { clinic } = useEffectiveClinic();
    const { items: cartItems, isLoading, updateQuantity, removeItem, clearAll, } = useCart();
    const [taxData, setTaxData] = useState(null);
    const [taxLoading, setTaxLoading] = useState(false);
    const [taxError, setTaxError] = useState(null);
    const [defaultShippingMethod, setDefaultShippingMethod] = useState(null);
    const [isLoadingDefaultShippingMethod, setIsLoadingDefaultShippingMethod] = useState(true);
    const handleLoadDefaultShippingMethod = useCallback(async () => {
        setIsLoadingDefaultShippingMethod(true);
        try {
            const response = await getShippingMethods({ is_default: true });
            const firstDefaultShippingMethod = response.body[0];
            if (firstDefaultShippingMethod) {
                setDefaultShippingMethod(firstDefaultShippingMethod);
            }
        }
        catch (err) {
            console.error('Failed to load default shipping method:', err);
        }
        finally {
            setIsLoadingDefaultShippingMethod(false);
        }
    }, []);
    const calculateArrivalDate = () => {
        const now = new Date();
        const arrivalDate = new Date(now.setDate(now.getDate() + 10));
        return arrivalDate.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
        });
    };
    // Calculate tax using TaxJar API
    const calculateTaxForCart = useCallback(async () => {
        if (!isAuthenticated || !clinic || !isClinicAuthorized) {
            setTaxError('User not authenticated or clinic is not authorized');
            return;
        }
        // Don't calculate tax if cart is empty
        if (!cartItems || cartItems.length === 0) {
            setTaxData(null);
            setTaxError(null);
            setTaxLoading(false);
            return;
        }
        setTaxLoading(true);
        setTaxError(null);
        try {
            if (!clinic.address) {
                setTaxError('Clinic address required for tax calculation');
                return;
            }
            const subTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
            console.log('Making TaxJar API call with clinic:', clinic);
            // Validate required address fields
            const address = clinic.address;
            const postalCode = address.postalCode || address.postal_code;
            const street = address.street1 || address.street_1;
            console.log('🔍 Full address object:', address);
            console.log('🔍 Address fields check:', {
                country: address.country,
                postalCode: address.postalCode,
                postal_code: address.postal_code,
                state: address.state,
                city: address.city,
                street1: address.street1,
                street_1: address.street_1,
            });
            if (!address.country || !postalCode || !address.state) {
                console.error('❌ Missing required address fields:', {
                    country: address.country,
                    postalCode: address.postalCode,
                    postal_code: address.postal_code,
                    state: address.state,
                });
                setTaxError('Invalid address data for tax calculation');
                return;
            }
            const taxRequest = {
                shipping_address: {
                    country: address.country,
                    zip: postalCode,
                    state: address.state,
                    city: address.city,
                    street,
                },
                amount: subTotal,
                shipping_amount: Number(defaultShippingMethod?.price || 0),
                items: cartItems.map((item) => ({ product_id: item.id, quantity: item.quantity })),
                shipping_method_id: defaultShippingMethod?.id,
            };
            console.log('🧾 Tax request for ShoppingCart:', taxRequest);
            console.log('🏥 Clinic address data:', clinic.address);
            console.log('📍 Shipping address details:', {
                country: address.country,
                zip: postalCode,
                state: address.state,
                city: address.city,
                street,
            });
            const response = await calculateTax(taxRequest);
            if (response.body.success) {
                setTaxData({
                    tax_amount: response.body.taxAmount || response.body.tax_amount,
                    tax_rate: response.body.taxRate || response.body.tax_rate,
                    subtotal: response.body.subtotal,
                    shipping_amount: Number(response.body.shippingAmount ?? response.body.shipping_amount ?? 0),
                    shipping_breakdown: normalizeShippingBreakdown(response.body.shippingBreakdown ?? response.body.shipping_breakdown),
                    total: response.body.total,
                });
            }
            else {
                throw new Error('Tax calculation failed');
            }
        }
        catch (error) {
            console.error('Error calculating tax:', error);
            setTaxError('Failed to calculate tax. Please try again.');
        }
        finally {
            setTaxLoading(false);
        }
    }, [
        isAuthenticated,
        clinic,
        isClinicAuthorized,
        cartItems,
        defaultShippingMethod?.price,
        defaultShippingMethod?.id,
    ]);
    useEffect(() => {
        handleLoadDefaultShippingMethod();
    }, [handleLoadDefaultShippingMethod]);
    // Recalculate tax when cart items change
    useEffect(() => {
        if (!isLoadingDefaultShippingMethod) {
            calculateTaxForCart();
        }
    }, [calculateTaxForCart, isLoadingDefaultShippingMethod]);
    const handleCheckout = () => {
        navigate('/app/orderDetails');
    };
    const handleQuantityChange = async (id, newQuantity) => {
        if (isLoading)
            return; // Prevents duplicate API calls
        if (newQuantity > 0) {
            await updateQuantity(id, newQuantity);
        }
        else {
            await removeItem(id);
        }
    };
    const handleRemoveItem = async (id) => {
        if (isLoading) {
            return;
        }
        try {
            await removeItem(id);
        }
        catch (err) {
            console.error('Failed to remove item from cart:', err);
        }
    };
    const handleEmptyCart = async () => {
        if (isLoading) {
            return;
        }
        try {
            await clearAll();
        }
        catch (err) {
            console.error('Failed to clear cart:', err);
        }
    };
    const subTotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
    const tax = taxData?.tax_amount ?? 0;
    const total = taxData?.total ?? subTotal;
    const totalItems = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    // Check if checkout should be disabled
    const isCheckoutDisabled = cartItems.length === 0 || !isAuthenticated || !clinic || isLoading || !isCheckoutAuthorized;
    return (_jsxs("main", { className: styles.cart, children: [_jsx("section", { className: styles.cartMain, children: _jsxs("section", { className: styles.cartWrapper, "aria-labelledby": "cart-heading", children: [_jsxs("header", { className: styles.cartHeadingWrapper, children: [_jsx("h1", { id: "cart-heading", children: "My Cart" }), _jsxs("p", { children: ["Arrives by ", calculateArrivalDate()] })] }), _jsx("div", { className: styles.cartDivider }), cartItems.length === 0 ? (_jsxs("div", { className: styles.emptyCartMessage, children: [_jsx("p", { children: "Your cart is empty" }), _jsx("p", { children: "Browse our products to add items to your cart" })] })) : (_jsx("ul", { className: styles.cartProducts, children: cartItems.map((item) => (_jsxs("li", { className: styles.cartProductRow, children: [_jsxs("div", { className: styles.cartProductInfo, children: [_jsx("img", { src: item.image, alt: item.name, width: "100", height: "100", className: styles.productImg }), _jsxs("div", { className: styles.cartProductData, children: [_jsx("p", { children: item.name }), _jsxs("div", { className: styles.cartCountContainer, children: [_jsx(CartCount, { value: item.quantity, onChange: (newQuantity) => handleQuantityChange(item.id, newQuantity), minValue: 1, disabled: isLoading }), _jsx("button", { type: "button", className: styles.cartEmptyButton, onClick: () => handleRemoveItem(item.id), disabled: isLoading, children: isLoading ? 'Updating...' : 'Remove' })] })] })] }), _jsx("div", { className: styles.cartProductPrice, children: formatCurrency(item.price * item.quantity) })] }, item.id))) })), cartItems.length > 0 && (_jsx("button", { type: "button", className: styles.removeLink, onClick: handleEmptyCart, disabled: isLoading, children: isLoading ? 'Updating...' : 'Empty My Cart' }))] }) }), _jsxs("aside", { className: styles.cartOrderDetails, "aria-labelledby": "order-summary-heading", children: [_jsx("h2", { id: "order-summary-heading", children: "Order Summary" }), _jsx("div", { className: styles.cartDivider }), cartItems.length === 0 ? (_jsx("div", { className: styles.emptyOrderSummary, children: _jsx("p", { children: "Your cart is empty" }) })) : (_jsxs("div", { children: [_jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsxs("p", { children: ["Total Items ", _jsxs("span", { children: ["(", totalItems, ")"] })] }), _jsx("p", { "data-testid": "summary-subtotal", children: formatCurrency(taxData?.subtotal || subTotal) })] }), _jsx("div", { className: `${styles.cartOrderDetailsItems} ${styles.shippingRow}`, children: _jsx(ShippingBreakdown, { entries: taxData?.shipping_breakdown ?? [], total: taxData?.shipping_amount ?? 0, loading: taxLoading || isLoadingDefaultShippingMethod, label: `${defaultShippingMethod ? `${defaultShippingMethod.estimatedDays} Day ` : ''}Shipping` }) }), _jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsx("p", { children: "Taxes" }), taxLoading ? _jsx("p", { "data-testid": "summary-tax", children: "Calculating..." }) : _jsx("p", { "data-testid": "summary-tax", children: formatCurrency(tax) })] }), taxError && (_jsx("div", { className: styles.cartOrderDetailsItems, children: _jsx("p", { style: { color: '#d32f2f', fontSize: '12px' }, children: taxError }) })), _jsxs("div", { className: styles.cartOrderDetailsItems, children: [_jsx("p", { children: "Order Total" }), _jsx("p", { "data-testid": "summary-total", children: formatCurrency(total) })] })] })), _jsx("div", { className: styles.buttonDivider }), _jsx(Button, { label: "Checkout", variant: "checkout", onClick: handleCheckout, disabled: isCheckoutDisabled }), isCheckoutDisabled && (_jsxs("div", { className: styles.checkoutDisabledMessage, children: [cartItems.length === 0 && _jsx("p", { children: "Add items to your cart to checkout" }), cartItems.length > 0 && !isAuthenticated && _jsx("p", { children: "Please log in to checkout" }), cartItems.length > 0 && isAuthenticated && isClinicAuthorized && !isCheckoutAuthorized && (_jsx("p", { children: "Your account is currently on hold. Please contact support to proceed with checkout." })), cartItems.length > 0 && isAuthenticated && !clinic && (_jsx("p", { children: "Please set up your clinic information to checkout" }))] }))] })] }));
};
export default ShoppingCart;
